import {FC, useCallback, useState} from 'react';
import {SurveyDataType} from 'api/types/LeadForm';
import useSurveyFormController from 'utils/hooks/useSurveyFormController';
import useAnalytics from 'utils/hooks/useAnalytics';
import {useSurveyNavigation} from 'utils/hooks/useNavigation';
import {useSetBarColor} from 'utils/hooks/useSetBarColor';
import {SurveyForm} from '@target-web/leadads-form-view';
import {assertGradient} from 'utils/utils';
import {bridge} from 'utils/bridge';
import {useSearchParams} from 'utils/searchParamsContext';
import {sendSurveyAnswer} from 'api/services/leads';
import {captureException} from '@sentry/react';
import {useLocalSessionId} from 'utils/hooks/useLocalSessionId';
import {AppProps} from './types';
import {PAGE_TYPE} from 'api/types/types';

type AppSurveyProps = AppProps & {
    formData: SurveyDataType;
};

const AppSurvey: FC<AppSurveyProps> = ({formData, layout, isShowCloseBtn}) => {
    const {formDataHash} = useSearchParams();
    const [page, setPage] = useState(formData.page.type === PAGE_TYPE.page ? formData.page.page : undefined);
    const [resultInfo, setResultInfo] = useState(
        formData.page.type === PAGE_TYPE.result_info ? formData.page.page : undefined,
    );
    const {sessionId, setSessionId} = useLocalSessionId(formDataHash);
    const formController = useSurveyFormController({leadForm: formData, page});
    const {clickToCallAnalyticEvent, clickToExternalLinkAnalyticEvent} = useAnalytics();
    const [isLoading, setIsLoading] = useState(false);

    const processNextQuestionOrResult: () => Promise<'' | 'success' | 'error'> = useCallback(() => {
        return new Promise(async (resolve, reject) => {
            setIsLoading(true);

            const {isValid} = formController?.validate() ?? {};

            if (isValid) {
                try {
                    const response = await sendSurveyAnswer(
                        formDataHash,
                        sessionId,
                        JSON.stringify({
                            answers: Object.keys(formController.formState.answers).map((blockId) => {
                                const {value} = formController.formState.answers[blockId];
                                const clonedValue = Object.assign({}, value);
                                if (clonedValue.answer_text) clonedValue.answer_text = clonedValue.answer_text.trim();
                                return {
                                    survey_id: formData.survey_id,
                                    page_id: page ? page.id : undefined,
                                    block_id: blockId,
                                    ...clonedValue,
                                };
                            }),
                        }),
                    );
                    if (!response.ok) {
                        throw new Error(response.statusText);
                    }
                    const result = await response.json();
                    if (result.type === PAGE_TYPE.result_info) {
                        setResultInfo(result.page);
                        resolve('success');
                    } else if (result.type === PAGE_TYPE.page) {
                        result.session_id && setSessionId(result.session_id);
                        setPage(result.page);
                        resolve('');
                    }
                } catch (e) {
                    captureException(e);
                    resolve('error');
                }
            }

            setIsLoading(false);
            reject('empty answer');
        });
    }, [formData, formDataHash, formController, page, sessionId, setSessionId]);

    const {history, goBack, goNext, closeForm, onTransition} = useSurveyNavigation({
        formData,
        processNextQuestionOrResult,
    });

    useSetBarColor();

    const {gradient, main_color, legal_doc_url, ...restFormData} = formData;

    return (
        <SurveyForm
            activePanelId={history[history.length - 1]}
            clickToCall={clickToCallAnalyticEvent}
            clickToExternalLink={clickToExternalLinkAnalyticEvent}
            formController={formController}
            goBack={goBack}
            goNext={goNext}
            closeForm={closeForm}
            history={history}
            layout={layout}
            leadForm={{
                ...restFormData,
                ...assertGradient(gradient, main_color),
                page,
                ...(resultInfo ? {result_info: resultInfo} : {}),
            }}
            isShowCloseBtn={isShowCloseBtn}
            onTransition={onTransition}
            isBridgeAvailable={!!bridge}
            isLoading={isLoading}
            agreementLink={legal_doc_url}
        />
    );
};

export default AppSurvey;
